import type {
  BroadcastRecipientsPage,
  CsvAudiencePreview,
  CsvAudienceSource,
  BroadcastReportView,
  BroadcastRetryResult,
  BroadcastView,
} from "../broadcast-types";
import type { LibraryTemplate, TemplateView } from "../template-types";
import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";

export type BroadcastsApiMethods = Pick<
  ApiClient,
  | "listTemplates"
  | "listTemplateLibrary"
  | "listBroadcasts"
  | "pauseBroadcast"
  | "resumeBroadcast"
  | "createBroadcast"
  | "previewBroadcastCsv"
  | "importBroadcastCsv"
  | "getMarketingApiEligibility"
  | "getBroadcast"
  | "updateBroadcast"
  | "scheduleBroadcast"
  | "getBroadcastReport"
  | "getBroadcastRecipients"
  | "retryFailedBroadcast"
  | "exportBroadcastReport"
  | "submitBroadcast"
  | "duplicateBroadcast"
  | "testSendBroadcast"
>;

export function createBroadcastsApi({
  request,
  requestCsv,
  ws,
  queryString,
}: HttpTransport): BroadcastsApiMethods {
  return {
    async listTemplates(id, query) {
      return (
        await request<{ items: TemplateView[] }>(
          "GET",
          `${ws(id)}/templates${queryString(query as Record<string, string | undefined> | undefined)}`,
        )
      ).items;
    },
    async listTemplateLibrary(id, channelId) {
      return (
        await request<{ items: LibraryTemplate[] }>(
          "GET",
          `${ws(id)}/templates/library${queryString({ channelId })}`,
        )
      ).items;
    },
    listBroadcasts: async (id) =>
      (await request<{ items: BroadcastView[] }>("GET", `${ws(id)}/broadcasts`)).items,
    pauseBroadcast: (id, bid) =>
      request<BroadcastView>("POST", `${ws(id)}/broadcasts/${encodeURIComponent(bid)}/pause`),
    resumeBroadcast: (id, bid) =>
      request<BroadcastView>("POST", `${ws(id)}/broadcasts/${encodeURIComponent(bid)}/resume`),
    previewBroadcastCsv: (id, input) =>
      request<CsvAudiencePreview>("POST", `${ws(id)}/broadcasts/csv-audience/preview`, input),
    importBroadcastCsv: (id, input) =>
      request<{ source: CsvAudienceSource; preview: CsvAudiencePreview }>(
        "POST",
        `${ws(id)}/broadcasts/csv-audience`,
        input,
      ),
    createBroadcast: (id, input) => request<BroadcastView>("POST", `${ws(id)}/broadcasts`, input),
    getMarketingApiEligibility: (id, channelId) =>
      request<{ eligible: boolean }>(
        "GET",
        `${ws(id)}/broadcasts/marketing-api-eligibility?channelId=${encodeURIComponent(channelId)}`,
      ),
    getBroadcast: (id, bid) =>
      request<BroadcastView>("GET", `${ws(id)}/broadcasts/${encodeURIComponent(bid)}`),
    updateBroadcast: (id, bid, patch) =>
      request<BroadcastView>("PATCH", `${ws(id)}/broadcasts/${encodeURIComponent(bid)}`, patch),
    scheduleBroadcast: (id, bid, scheduledAt) =>
      request<BroadcastView>("POST", `${ws(id)}/broadcasts/${encodeURIComponent(bid)}/schedule`, {
        scheduledAt,
      }),
    getBroadcastReport: (id, bid) =>
      request<BroadcastReportView>("GET", `${ws(id)}/broadcasts/${encodeURIComponent(bid)}/report`),
    getBroadcastRecipients: (id, bid, query) => {
      const qs = new URLSearchParams();
      if (query?.cursor) qs.set("cursor", query.cursor);
      if (query?.limit) qs.set("limit", String(query.limit));
      if (query?.status) qs.set("status", query.status);
      if (query?.reason) qs.set("reason", query.reason);
      const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
      return request<BroadcastRecipientsPage>(
        "GET",
        `${ws(id)}/broadcasts/${encodeURIComponent(bid)}/recipients${suffix}`,
      );
    },
    duplicateBroadcast: (id, bid) =>
      request<BroadcastView>("POST", `${ws(id)}/broadcasts/${encodeURIComponent(bid)}/duplicate`),
    testSendBroadcast: (id, bid, phone) =>
      request<{ to: string }>("POST", `${ws(id)}/broadcasts/${encodeURIComponent(bid)}/test-send`, {
        phone,
      }),
    retryFailedBroadcast: (id, bid, failureCodes) =>
      request<BroadcastRetryResult>(
        "POST",
        `${ws(id)}/broadcasts/${encodeURIComponent(bid)}/retry-failed`,
        failureCodes && failureCodes.length > 0 ? { failureCodes } : {},
      ),
    exportBroadcastReport: (id, bid) =>
      requestCsv("GET", `${ws(id)}/broadcasts/${encodeURIComponent(bid)}/report.csv`),
    submitBroadcast: (id, bid) =>
      request<BroadcastView>("POST", `${ws(id)}/broadcasts/${encodeURIComponent(bid)}/submit`),
  };
}
